import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { MapPin, Plus, Edit, Trash2, Upload } from 'lucide-react';

const AreasComuns = () => {
  const { user } = useAuth();
  const { condominioSelecionado, getCondominioId, isAdministrador } = useCondominio();
  const [areasComuns, setAreasComuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    area_comum_nome: '',
    area_comum_tipo: '',
    area_comum_descricao: '',
    area_comum_capacidade: '',
    area_comum_valor_hora: '',
    area_comum_valor_por_hora: true, // true = por hora, false = valor total
    area_comum_permite_reserva: true,
    area_comum_horario_inicio: '06:00',
    area_comum_horario_fim: '22:00',
    area_comum_dias_funcionamento: ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'],
    area_comum_antecipacao_maxima: 30,
    area_comum_antecipacao_minima: 1,
    area_comum_duracao_maxima: 4,
    area_comum_duracao_minima: 1,
    area_comum_equipamentos: [],
    area_comum_regras: '',
    area_comum_ativo: true
  });

  useEffect(() => {
    const condominioId = getCondominioId();
    if (condominioId) {
      loadAreasComuns();
      loadTipos();
    }
  }, [condominioSelecionado]);

  const loadAreasComuns = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      if (!condominioId) {
        setAreasComuns([]);
        return;
      }
      
      const response = await api.get(`/areas-comuns?condominio_id=${condominioId}`);
      setAreasComuns(response.data.data.areasComuns || []);
    } catch (error) {
      console.error('Erro ao carregar áreas comuns:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTipos = async () => {
    try {
      const response = await api.get('/areas-comuns/tipos/disponiveis');
      setTipos(response.data.data.tipos || []);
    } catch (error) {
      console.error('Erro ao carregar tipos:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsSubmitting(true);
    
    try {
      // Criar FormData para permitir upload de imagem
      const formDataToSend = new FormData();
      
      // Adicionar todos os campos do formulário
      formDataToSend.append('area_comum_nome', formData.area_comum_nome);
      formDataToSend.append('area_comum_tipo', formData.area_comum_tipo);
      if (formData.area_comum_descricao) {
        formDataToSend.append('area_comum_descricao', formData.area_comum_descricao);
      }
      if (formData.area_comum_capacidade) {
        formDataToSend.append('area_comum_capacidade', parseInt(formData.area_comum_capacidade));
      }
      if (formData.area_comum_valor_hora) {
        // Converter valor formatado (R$ 0,00) para número
        const numericValue = parseMoneyValue(formData.area_comum_valor_hora);
        formDataToSend.append('area_comum_valor_hora', parseFloat(numericValue));
      }
      formDataToSend.append('area_comum_permite_reserva', formData.area_comum_permite_reserva);
      formDataToSend.append('area_comum_valor_por_hora', formData.area_comum_valor_por_hora);
      formDataToSend.append('area_comum_horario_inicio', formData.area_comum_horario_inicio);
      formDataToSend.append('area_comum_horario_fim', formData.area_comum_horario_fim);
      formDataToSend.append('area_comum_dias_funcionamento', JSON.stringify(formData.area_comum_dias_funcionamento));
      formDataToSend.append('area_comum_antecipacao_maxima', formData.area_comum_antecipacao_maxima ? parseInt(formData.area_comum_antecipacao_maxima) : 30);
      formDataToSend.append('area_comum_antecipacao_minima', formData.area_comum_antecipacao_minima ? parseInt(formData.area_comum_antecipacao_minima) : 1);
      formDataToSend.append('area_comum_duracao_maxima', formData.area_comum_duracao_maxima ? parseInt(formData.area_comum_duracao_maxima) : 4);
      formDataToSend.append('area_comum_duracao_minima', formData.area_comum_duracao_minima ? parseInt(formData.area_comum_duracao_minima) : 1);
      if (Array.isArray(formData.area_comum_equipamentos)) {
        formDataToSend.append('area_comum_equipamentos', JSON.stringify(formData.area_comum_equipamentos));
      }
      if (formData.area_comum_regras) {
        formDataToSend.append('area_comum_regras', formData.area_comum_regras);
      }
      formDataToSend.append('area_comum_ativo', formData.area_comum_ativo);
      formDataToSend.append('condominio_id', getCondominioId());
      
      // Adicionar imagem se selecionada
      if (selectedImage) {
        formDataToSend.append('imagem', selectedImage);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      if (editingArea) {
        await api.put(`/areas-comuns/${editingArea.area_comum_id}`, formDataToSend, config);
      } else {
        await api.post('/areas-comuns', formDataToSend, config);
      }
      setShowModal(false);
      setEditingArea(null);
      resetForm();
      loadAreasComuns();
    } catch (error) {
      console.error('Erro ao salvar área comum:', error);
      
      // Tratar diferentes tipos de erro
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Se houver múltiplos erros de validação
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        setErrorMessage(errorMessages);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Erro interno do servidor. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (area) => {
    setEditingArea(area);
    // Formatar valor monetário se existir
    let valorFormatado = '';
    if (area.area_comum_valor_hora && parseFloat(area.area_comum_valor_hora) > 0) {
      valorFormatado = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(parseFloat(area.area_comum_valor_hora));
    }
    
    setFormData({
      area_comum_nome: area.area_comum_nome || '',
      area_comum_tipo: area.area_comum_tipo || '',
      area_comum_descricao: area.area_comum_descricao || '',
      area_comum_capacidade: area.area_comum_capacidade || '',
      area_comum_valor_hora: valorFormatado,
      area_comum_valor_por_hora: area.area_comum_valor_por_hora !== false, // Default true se não existir
      area_comum_permite_reserva: area.area_comum_permite_reserva !== false,
      area_comum_horario_inicio: area.area_comum_horario_inicio || '06:00',
      area_comum_horario_fim: area.area_comum_horario_fim || '22:00',
      area_comum_dias_funcionamento: area.area_comum_dias_funcionamento || ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'],
      area_comum_antecipacao_maxima: area.area_comum_antecipacao_maxima || 30,
      area_comum_antecipacao_minima: area.area_comum_antecipacao_minima || 1,
      area_comum_duracao_maxima: area.area_comum_duracao_maxima || 4,
      area_comum_duracao_minima: area.area_comum_duracao_minima || 1,
      area_comum_equipamentos: area.area_comum_equipamentos || [],
      area_comum_regras: area.area_comum_regras || '',
      area_comum_ativo: area.area_comum_ativo !== false
    });
    
    // Carregar preview da imagem se existir
    if (area.area_imagem_caminho) {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      let imagePath = area.area_imagem_caminho;
      
      // Remover caminho completo se existir, manter apenas a parte relativa
      if (imagePath.includes('uploads')) {
        // Extrair apenas a parte após 'uploads/'
        const match = imagePath.match(/uploads[/\\](.+)$/);
        imagePath = match ? match[1] : imagePath.split(/[/\\]/).slice(-2).join('/');
      } else if (!imagePath.startsWith('areas-comuns/')) {
        // Se não começa com 'areas-comuns/', assumir que é apenas o filename
        imagePath = `areas-comuns/${imagePath}`;
      }
      
      const imageUrl = `${baseUrl}/uploads/${imagePath}`;
      setImagePreview(imageUrl);
    } else {
      setImagePreview(null);
    }
    setSelectedImage(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta área comum?')) {
      try {
        await api.delete(`/areas-comuns/${id}`);
        loadAreasComuns();
      } catch (error) {
        console.error('Erro ao excluir área comum:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      area_comum_nome: '',
      area_comum_tipo: '',
      area_comum_descricao: '',
      area_comum_capacidade: '',
      area_comum_valor_hora: '',
      area_comum_valor_por_hora: true,
      area_comum_permite_reserva: true,
      area_comum_horario_inicio: '06:00',
      area_comum_horario_fim: '22:00',
      area_comum_dias_funcionamento: ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'],
      area_comum_antecipacao_maxima: 30,
      area_comum_antecipacao_minima: 1,
      area_comum_duracao_maxima: 4,
      area_comum_duracao_minima: 1,
      area_comum_equipamentos: [],
      area_comum_regras: '',
      area_comum_ativo: true
    });
    setSelectedImage(null);
    setImagePreview(null);
    setErrorMessage('');
  };

  // Função para aplicar máscara de valor monetário
  const applyMoneyMask = (value) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    if (!numbers) return '';
    
    // Converte para centavos e depois formata
    const cents = parseInt(numbers, 10);
    const real = cents / 100;
    
    // Formata como moeda brasileira
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(real);
  };

  // Função para converter valor formatado para número
  const parseMoneyValue = (value) => {
    if (!value || value.trim() === '') return '';
    // Remove formatação e converte para número
    const numbers = value.replace(/\D/g, '');
    if (!numbers || numbers === '0') return '';
    const cents = parseInt(numbers, 10);
    const real = cents / 100;
    return real.toString();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Aplicar máscara monetária para o campo de valor
    if (name === 'area_comum_valor_hora') {
      const maskedValue = applyMoneyMask(value);
      setFormData(prev => ({
        ...prev,
        [name]: maskedValue
      }));
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEquipamentoChange = (e) => {
    const value = e.target.value;
    // Se o campo estiver vazio, definir como array vazio
    if (!value.trim()) {
      setFormData(prev => ({
        ...prev,
        area_comum_equipamentos: []
      }));
      return;
    }
    
    // Dividir por vírgula e limpar espaços
    const equipamentos = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
    setFormData(prev => ({
      ...prev,
      area_comum_equipamentos: equipamentos
    }));
  };

  const getTipoLabel = (tipo) => {
    const tipoObj = tipos.find(t => t.value === tipo);
    return tipoObj ? tipoObj.label : tipo;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <MapPin className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Áreas Comuns</h1>
        </div>
        {(['sindico', 'subsindico', 'administrador'].includes(user?.tipo) && (!isAdministrador() || getCondominioId())) ? (
          <button
            onClick={() => {
              setEditingArea(null);
              resetForm();
              setShowModal(true);
            }}
            className="btn btn-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Nova Área Comum
          </button>
        ) : isAdministrador() && !getCondominioId() ? (
          <div className="text-sm text-gray-500">
            Selecione um condomínio para gerenciar áreas comuns
          </div>
        ) : null}
      </div>

      {!getCondominioId() && isAdministrador() ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <MapPin className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Selecione um condomínio
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Para visualizar e gerenciar áreas comuns, selecione um condomínio no menu superior.</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Lista de Áreas Comuns */}
          <div className="card">
        <div className="card-body">
          <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Capacidade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor/Hora
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Horário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {areasComuns.map((area) => (
                <tr key={area.area_comum_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      {area.area_imagem_caminho && (() => {
                        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                        // O caminho já vem como 'areas-comuns/filename' do banco, usar diretamente
                        const imageUrl = `${baseUrl}/uploads/${area.area_imagem_caminho}`;
                        
                        return (
                          <div className="flex-shrink-0">
                            <img
                              src={imageUrl}
                              alt={area.area_comum_nome}
                              className="h-12 w-12 rounded object-cover"
                              onError={(e) => {
                                console.error('❌ Erro ao carregar imagem:', {
                                  url: imageUrl,
                                  caminho_banco: area.area_imagem_caminho,
                                  elemento: e.target
                                });
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => {
                                console.log('✅ Imagem carregada:', imageUrl);
                              }}
                            />
                          </div>
                        );
                      })()}
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {area.area_comum_nome}
                        </div>
                        {area.area_comum_descricao && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {area.area_comum_descricao}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {getTipoLabel(area.area_comum_tipo)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {area.area_comum_capacidade ? `${area.area_comum_capacidade} pessoas` : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {area.area_comum_valor_hora ? (
                      `R$ ${parseFloat(area.area_comum_valor_hora).toFixed(2)}${area.area_comum_valor_por_hora !== false ? '/hora' : ' (total)'}`
                    ) : 'Gratuito'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {area.area_comum_horario_inicio} - {area.area_comum_horario_fim}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        area.area_comum_ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {area.area_comum_ativo ? 'Ativa' : 'Inativa'}
                      </span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        area.area_comum_permite_reserva 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {area.area_comum_permite_reserva ? 'Reservável' : 'Não Reservável'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(area)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {['sindico', 'administrador'].includes(user?.tipo) && (
                        <button
                          onClick={() => handleDelete(area.area_comum_id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container custom-scrollbar">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingArea ? 'Editar Área Comum' : 'Nova Área Comum'}
              </h3>
              
              {/* Exibir mensagem de erro */}
              {errorMessage && (
                <div className="mb-4 alert-error border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Erro ao salvar área comum
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{errorMessage}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 grid-cols-2 gap-4">
                  {/* Dados Básicos */}
                  <div className="col-span-2">
                    <h4 className="text-md font-semibold text-gray-700 mb-2">Dados Básicos</h4>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nome *</label>
                    <input
                      type="text"
                      name="area_comum_nome"
                      value={formData.area_comum_nome}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tipo *</label>
                    <select
                      name="area_comum_tipo"
                      value={formData.area_comum_tipo}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Selecione o tipo</option>
                      {tipos.map(tipo => (
                        <option key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Descrição</label>
                    <textarea
                      name="area_comum_descricao"
                      value={formData.area_comum_descricao}
                      onChange={handleInputChange}
                      rows="3"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Foto da Área Comum</label>
                    <div className="file-input-container">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        id="area-comum-foto"
                      />
                      <label htmlFor="area-comum-foto" className="file-input-label">
                        <Upload className="h-4 w-4" style={{marginRight: '0.5rem'}} />
                        {selectedImage ? 'Arquivo selecionado' : 'Clique para selecionar uma foto'}
                      </label>
                    </div>
                    {imagePreview && (
                      <div className="file-preview">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ maxWidth: '300px', maxHeight: '200px', marginTop: '10px', borderRadius: '4px' }}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Capacidade</label>
                    <input
                      type="number"
                      name="area_comum_capacidade"
                      value={formData.area_comum_capacidade}
                      onChange={handleInputChange}
                      min="1"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <div className="flex items-center mb-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="area_comum_valor_por_hora"
                          checked={formData.area_comum_valor_por_hora}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Valor por Hora</span>
                      </label>
                    </div>
                    <label className="block text-sm font-medium text-gray-700">
                      {formData.area_comum_valor_por_hora ? 'Valor por Hora (R$)' : 'Valor Total (R$)'}
                    </label>
                    <input
                      type="text"
                      name="area_comum_valor_hora"
                      value={formData.area_comum_valor_hora}
                      onChange={handleInputChange}
                      placeholder="0,00"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Horários */}
                  <div className="col-span-2">
                    <h4 className="text-md font-semibold text-gray-700 mb-2 mt-4">Horários</h4>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Horário de Funcionamento</label>
                    <div className="flex space-x-2 mt-1">
                      <input
                        type="time"
                        name="area_comum_horario_inicio"
                        value={formData.area_comum_horario_inicio}
                        onChange={handleInputChange}
                        className="block border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      <span className="self-center">às</span>
                      <input
                        type="time"
                        name="area_comum_horario_fim"
                        value={formData.area_comum_horario_fim}
                        onChange={handleInputChange}
                        className="block border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Configurações de Reserva */}
                  <div className="col-span-2">
                    <h4 className="text-md font-semibold text-gray-700 mb-2 mt-4">Configurações de Reserva</h4>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Antecipação Mínima (dias)</label>
                    <input
                      type="number"
                      name="area_comum_antecipacao_minima"
                      value={formData.area_comum_antecipacao_minima}
                      onChange={handleInputChange}
                      min="0"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Antecipação Máxima (dias)</label>
                    <input
                      type="number"
                      name="area_comum_antecipacao_maxima"
                      value={formData.area_comum_antecipacao_maxima}
                      onChange={handleInputChange}
                      min="1"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Duração Mínima (horas)</label>
                    <input
                      type="number"
                      name="area_comum_duracao_minima"
                      value={formData.area_comum_duracao_minima}
                      onChange={handleInputChange}
                      min="0.5"
                      step="0.5"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Duração Máxima (horas)</label>
                    <input
                      type="number"
                      name="area_comum_duracao_maxima"
                      value={formData.area_comum_duracao_maxima}
                      onChange={handleInputChange}
                      min="1"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Equipamentos Disponíveis</label>
                    <input
                      type="text"
                      value={Array.isArray(formData.area_comum_equipamentos) ? formData.area_comum_equipamentos.join(', ') : ''}
                      onChange={handleEquipamentoChange}
                      placeholder="Ex: bola, coletes, mesas (separados por vírgula)"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Digite os equipamentos separados por vírgula. Ex: bola, coletes, mesas
                    </p>
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Regras de Uso</label>
                    <textarea
                      name="area_comum_regras"
                      value={formData.area_comum_regras}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Regras específicas para uso desta área comum..."
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="area_comum_permite_reserva"
                          checked={formData.area_comum_permite_reserva}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Permite Reservas</span>
                      </label>
                      
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="area_comum_ativo"
                          checked={formData.area_comum_ativo}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700">Ativa</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingArea(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 rounded-md text-white ${
                      isSubmitting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isSubmitting ? 'Salvando...' : (editingArea ? 'Atualizar' : 'Criar')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AreasComuns;
